import React from 'react';
import PropTypes from 'prop-types';
import { Users } from 'lucide-react';
import styles from './ProgramViewer.module.css';
import useGroupLabels from 'hooks/useGroupLabels';
import { combineTickLabel } from 'utils/combineAges';

const ProgramViewer = ({ programData }) => {
  // A row with Combine ages ticked (step 8) is marked with the tick's words.
  const { groupingBasis } = useGroupLabels();

  // Helper function to determine event type class
  const getEventTypeClass = (eventType) => {
    if (eventType >= 1000) return styles.break;
    if ([1, 2, 3, 4, 6].includes(eventType)) return styles.training;
    if ([20, 21, 22, 24, 25, 37].includes(eventType)) return styles.sprint;
    if ([61, 62, 63, 64, 65, 66, 67, 68].includes(eventType))
      return styles.field;
    if ([31, 32, 33, 36, 38, 39].includes(eventType)) return styles.distance;
    if ([27, 28, 34, 35].includes(eventType)) return styles.relay;
    if ([15, 16, 17, 18, 19, 23, 26, 29, 69].includes(eventType))
      return styles.hurdles;
    if ([41, 42, 43, 44, 45].includes(eventType)) return styles.walk;
    return '';
  };

  return (
    <div className={styles.programView}>
      <table className={styles.programTable}>
        <thead>
          <tr>
            <th>Event Program</th>
            {[...Array(programData.rotationCount)].map((_, i) => (
              <th key={i}>Event {i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody id="program-tbody">
          {programData.programGroups.map((group, i) => (
            <tr key={i}>
              <td>
                <div className={styles.groupName}>
                  <Users className={styles.groupIcon} size={16} />
                  {/* The name, with the Combine ages mark under it (Doug, 2026-10-14) */}
                  <div className={styles.groupNameText}>
                    <span>{group.displayName}</span>
                    {group.combineAges && (
                      <span
                        className={styles.combinedMark}
                        title="This row's events give one set of places for the whole group"
                      >
                        ✓ {combineTickLabel(groupingBasis)}
                      </span>
                    )}
                  </div>
                </div>
              </td>
              {[...Array(programData.rotationCount)].map((_, j) => (
                <td key={j}>
                  <div
                    className={`${styles.eventCell} ${getEventTypeClass(group.events[j].eventType)}`}
                  >
                    <span className={styles.eventName}>
                      {group.events[j].eventTypeName}
                    </span>
                    {group.events[j].location && (
                      <span className={styles.eventLocation}>
                        {group.events[j].location}
                      </span>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

ProgramViewer.propTypes = {
  programData: PropTypes.shape({
    rotationCount: PropTypes.number,
    programGroups: PropTypes.arrayOf(
      PropTypes.shape({
        groupSource: PropTypes.string,
        groupName: PropTypes.string,
        ageRange: PropTypes.string,
        gender: PropTypes.oneOf(['Mixed', 'M', 'F']),
        displayOrder: PropTypes.number,
        sourceGroupID: PropTypes.string,
        combineAges: PropTypes.bool,
        events: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  }),
};

export default ProgramViewer;
